import type { WebsiteStatus } from '../../types/api';

interface BadgeProps {
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  children: React.ReactNode;
  className?: string;
}

const variantStyles = {
  default: 'bg-gray-100 text-gray-700',
  success: 'bg-green-100 text-green-700',
  warning: 'bg-yellow-100 text-yellow-700',
  error: 'bg-red-100 text-red-700',
  info: 'bg-blue-100 text-blue-700',
};

export function Badge({ variant = 'default', children, className = '' }: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variantStyles[variant]} ${className}`}
    >
      {children}
    </span>
  );
}

// Status-specific badge with automatic color mapping
interface StatusBadgeProps {
  status: WebsiteStatus;
  className?: string;
}

const statusVariants: Record<WebsiteStatus, BadgeProps['variant']> = {
  pending: 'default',
  generating: 'info',
  'pending-tech-qa': 'info',
  'tech-qa-failed': 'error',
  'pending-internal-qa': 'warning',
  'needs-internal-revision': 'warning',
  'pending-customer-review': 'info',
  'needs-customer-revision': 'warning',
  'approved-to-launch': 'success',
  active: 'success',
  live: 'success',
  suspended: 'error',
  'generation-failed': 'error',
};

const statusLabels: Record<WebsiteStatus, string> = {
  pending: 'Pending',
  generating: 'Generating',
  'pending-tech-qa': 'Tech QA',
  'tech-qa-failed': 'Tech QA Failed',
  'pending-internal-qa': 'Internal QA',
  'needs-internal-revision': 'Needs Revision',
  'pending-customer-review': 'Customer Review',
  'needs-customer-revision': 'Customer Changes',
  'approved-to-launch': 'Approved to Launch',
  active: 'Active',
  live: 'Live',
  suspended: 'Suspended',
  'generation-failed': 'Failed',
};

export function StatusBadge({ status, className = '' }: StatusBadgeProps) {
  return (
    <Badge variant={statusVariants[status]} className={className}>
      {statusLabels[status]}
    </Badge>
  );
}
